// WWI observation post: a tall slender timber lookout tower on four braced
// legs, an open railed platform under a khaki camo-net pyramid roof, and a
// ladder up the front face. 1x1 tile.
import { defineModel, climb, styleV3, type Part } from "../../lib";

const { box, pyramid, slab, poles } = styleV3;

const KHAKI = "#7a6f4e";       // the camo-net roof
const MUD = "#5d4a33";
const TIMBER = "#8b6438";
const TIMBER_DK = "#6a4c2a";

const BASE = 0.12;                       // foundation slab top
const LEG = 0.26;                        // leg offset from centre
const LEG_H = 1.0;                       // full leg height above the slab
const PLAT_Y = BASE + LEG_H + 0.03;      // platform centre
const LADDER_Z = 0.36;                   // the ladder stands off the +z face

function legs(h: number): Part[] {
  const out: Part[] = [];
  for (const [x, z] of [[-LEG, -LEG], [LEG, -LEG], [-LEG, LEG], [LEG, LEG]] as const) {
    out.push(box(0.06, h, 0.06, TIMBER, [x, BASE + h / 2, z]));
  }
  return out;
}

// A horizontal brace ring tying the four legs together at height `y`.
function braceRing(y: number): Part[] {
  return [
    box(0.56, 0.04, 0.04, TIMBER_DK, [0, y, -LEG]),
    box(0.56, 0.04, 0.04, TIMBER_DK, [0, y, LEG]),
    box(0.04, 0.04, 0.56, TIMBER_DK, [-LEG, y, 0]),
    box(0.04, 0.04, 0.56, TIMBER_DK, [LEG, y, 0]),
  ];
}

// Diagonal braces across the front and back faces.
function diagonals(): Part[] {
  return [
    box(0.035, 0.7, 0.035, TIMBER_DK, [0, 0.5, LEG + 0.01], [0, 0, 0.7]),
    box(0.035, 0.7, 0.035, TIMBER_DK, [0, 0.5, -LEG - 0.01], [0, 0, -0.7]),
  ];
}

function platform(): Part {
  return box(0.68, 0.06, 0.68, TIMBER_DK, [0, PLAT_Y, 0]);
}

// The open lookout: corner posts and rails around the platform edge.
function railing(): Part[] {
  const out: Part[] = [];
  for (const [x, z] of [[-0.31, -0.31], [0.31, -0.31], [-0.31, 0.31], [0.31, 0.31]] as const) {
    out.push(box(0.035, 0.2, 0.035, TIMBER, [x, PLAT_Y + 0.13, z]));
  }
  out.push(box(0.66, 0.035, 0.035, TIMBER, [0, PLAT_Y + 0.21, -0.31]));
  out.push(box(0.66, 0.035, 0.035, TIMBER, [0, PLAT_Y + 0.21, 0.31]));
  out.push(box(0.035, 0.035, 0.66, TIMBER, [-0.31, PLAT_Y + 0.21, 0]));
  out.push(box(0.035, 0.035, 0.66, TIMBER, [0.31, PLAT_Y + 0.21, 0]));
  return out;
}

// The khaki camo-net roof on four thin posts, with a mud net-fringe band.
function camoRoof(): Part[] {
  const roofBase = PLAT_Y + 0.55;
  const out: Part[] = [];
  for (const [x, z] of [[-0.28, -0.28], [0.28, -0.28], [-0.28, 0.28], [0.28, 0.28]] as const) {
    out.push(box(0.03, 0.34, 0.03, TIMBER, [x, PLAT_Y + 0.38, z]));
  }
  out.push(box(0.8, 0.05, 0.8, MUD, [0, roofBase - 0.02, 0]));
  out.push(pyramid(0.78, 0.24, KHAKI, 0, roofBase, 0));
  return out;
}

// The ladder up the +z face: two rails and six rungs.
function ladder(): Part[] {
  const out: Part[] = [
    box(0.035, 1.06, 0.035, TIMBER, [-0.1, BASE + 0.53, LADDER_Z]),
    box(0.035, 1.06, 0.035, TIMBER, [0.1, BASE + 0.53, LADDER_Z]),
  ];
  for (let i = 0; i < 6; i++) {
    out.push(box(0.2, 0.03, 0.03, TIMBER_DK, [0, BASE + 0.14 + i * 0.17, LADDER_Z]));
  }
  return out;
}

export const observationPost = defineModel({
  id: "observation-post",
  tiles: { w: 1, h: 1 },
  batchId: "wwi",
  name: "Observation Post",
  tags: ["building", "wwi", "military", "tower", "lookout", "observation"],
  summary: "A tall slender timber lookout tower under a khaki camo-net roof.",
  description:
    "Timber observation tower on four braced legs: a ladder up the front, an " +
    "open railed lookout platform, and a khaki camo-net pyramid roof with a " +
    "mud fringe. A spotter climbs to the platform to watch the line.",
  author: "Fable 5",
  footprint: 0.5,
  defaultVariant: "complete",
  variants: [
    { id: "foundation", label: "Foundation", parts: () => [slab(0.9, 0.9)] },
    { id: "lower", label: "Lower", parts: () => [
      slab(0.9, 0.9), ...legs(0.5), ...braceRing(0.42), ...poles(0.78, 0.78, 0.7),
    ] },
    { id: "upper", label: "Upper", parts: () => [
      slab(0.9, 0.9), ...legs(LEG_H), ...braceRing(0.55), ...diagonals(), platform(),
      ...poles(0.78, 0.78, 1.35),
    ] },
    { id: "complete", label: "Complete", parts: () => [
      slab(0.9, 0.9), ...legs(LEG_H), ...braceRing(0.55), ...diagonals(), platform(),
      ...railing(), ...camoRoof(), ...ladder(),
    ] },
  ],
  animations: [],
  // Where the spotter starts the climb: at the ladder foot, facing the tower.
  stations: [
    { id: "lookout", position: [0, BASE, LADDER_Z + 0.06], facing: Math.PI, description: "The ladder foot where a spotter starts the climb." },
  ],
  interactions: [
    { id: "climb-lookout", label: "Climb to the lookout", station: "lookout", variants: ["complete"], ...climb() },
  ],
});
